import { isSuccess } from '@carvana/merch-result';
import {
  calculateGetItBy,
  calculateSoonestAvailable
} from '@carvana/merch-tile/utils/availability';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import AvailabilityContext, {
  type PageAvailabilityData
} from '~/context/home/AvailabilityAndPriceContext';
import type { MegaEventVehicle, RecommendedVehicle } from '~/contracts/common';
import type { CarouselVehicles } from '~/contracts/home';
import getPurchaseStatus from '~/utilities/getPurchaseStatus';

type BelongsTo = keyof CarouselVehicles | 'recent' | 'suggestedForYou';

type MegaEventVehicleWithBelongsTo = MegaEventVehicle & {
  belongsTo: BelongsTo;
};

const getVehicleDataFromRecommendation =
  (belongsTo: BelongsTo) =>
  (v: RecommendedVehicle, index: number): MegaEventVehicleWithBelongsTo => {
    const downPayment = v.price?.downPaymentDetails?.totalCashDue;
    const hasTerms = downPayment != null && v.price.monthlyPayment != null;

    const { isLocked, isOnHold } = getPurchaseStatus(v);

    // Note: Delivery/pickup info will be added separately
    return {
      belongsTo,
      tileIndex: index,
      tileInfo: {
        apr: v.price.apr,
        discounts: v.price.discounts,
        downPayment,
        // estimatedTaxesAndFees: ,
        hasTerms,
        isLocked,
        isOnHold,
        monthlyPayment: v.price.monthlyPayment,
        nonRefundableShippingFees:
          v.price.downPaymentDetails?.nonRefundableShippingFees,
        pricingErrorMessage: v.price.pricingErrorMessage,
        shippingCost: v.price.transportCost,
        stickerPrice: v.price.total,
        term: v.price.term
      },
      vehicleId: v.vehicleId,
      vehicleInventoryType: v.vehicleInventoryType,
      // vehicleLockType: ,
      vehiclePurchaseType: v.vehiclePurchaseType,
      vehicleUnlockDateTime: v.vehicleUnlockDateTime
    } satisfies MegaEventVehicleWithBelongsTo;
  };

const populateAvailabilityInfo =
  (pageAvailability: PageAvailabilityData) =>
  (v: MegaEventVehicleWithBelongsTo) => {
    if (!v.vehicleId || !pageAvailability[v.vehicleId]) return v;

    const { soonestDelivery, soonestPickup } = pageAvailability[v.vehicleId];
    const deliveryInfo = isSuccess(soonestDelivery)
      ? soonestDelivery.content
      : undefined;
    const pickupInfo = isSuccess(soonestPickup)
      ? soonestPickup.content
      : undefined;

    const { activityType, getItBy, routeId, utcSoonestDateTimeIso } =
      calculateGetItBy(
        calculateSoonestAvailable({
          delivery: deliveryInfo,
          pickup: pickupInfo
        })
      );

    return {
      ...v,
      deliveryInfo,
      pickupInfo,
      tileInfo: {
        ...v.tileInfo,
        activityType,
        getItBy,
        getItByDateIso: utcSoonestDateTimeIso,
        routeId
      }
    } satisfies MegaEventVehicleWithBelongsTo;
  };

type ContextType = {
  setCarouselVehicles: (_: CarouselVehicles) => void;
  setRecentVehicles: (_: RecommendedVehicle[]) => void;
  setSuggestedVehicles: (_: RecommendedVehicle[]) => void;
  triggerVehicleEvent: number;
  vehicles: MegaEventVehicleWithBelongsTo[];
};

const defaultContext: ContextType = {
  setCarouselVehicles() {},
  setRecentVehicles() {},
  setSuggestedVehicles() {},
  triggerVehicleEvent: 0,
  vehicles: []
};

const MegaEventContext = createContext(defaultContext);

export function MegaEventContextProvider({
  children
}: React.PropsWithChildren) {
  const { pageAvailability } = useContext(AvailabilityContext);
  const [carouselVehicles, setCarouselVehiclesInternal] = useState<
    MegaEventVehicleWithBelongsTo[]
  >([]);
  const [recentVehicles, setRecentVehiclesInternal] = useState<
    MegaEventVehicleWithBelongsTo[]
  >([]);
  const [suggestedVehicles, setSuggestedVehiclesInternal] = useState<
    MegaEventVehicleWithBelongsTo[]
  >([]);
  const [triggerVehicleEvent, setTriggerVehicleEvent] = useState(
    defaultContext.triggerVehicleEvent
  );

  const setCarouselVehicles = useCallback((cv: CarouselVehicles) => {
    setCarouselVehiclesInternal(
      Object.entries(cv).reduce<MegaEventVehicleWithBelongsTo[]>(
        (acc, [key, value]) =>
          acc.concat(
            value.map(
              getVehicleDataFromRecommendation(key as keyof CarouselVehicles)
            )
          ),
        []
      )
    );
  }, []);

  const setRecentVehicles = useCallback((rv: RecommendedVehicle[]) => {
    setRecentVehiclesInternal(
      rv.map(getVehicleDataFromRecommendation('recent'))
    );
  }, []);

  const setSuggestedVehicles = useCallback((sv: RecommendedVehicle[]) => {
    setSuggestedVehiclesInternal(
      sv.map(getVehicleDataFromRecommendation('suggestedForYou'))
    );
  }, []);

  // Add delivery/pickup info to vehicles
  useEffect(() => {
    if (!pageAvailability.isLoaded) return;

    setCarouselVehiclesInternal(prev =>
      prev.map(populateAvailabilityInfo(pageAvailability.data))
    );

    setRecentVehiclesInternal(prev =>
      prev.map(populateAvailabilityInfo(pageAvailability.data))
    );

    setSuggestedVehiclesInternal(prev =>
      prev.map(populateAvailabilityInfo(pageAvailability.data))
    );

    setTriggerVehicleEvent(Date.now());
  }, [pageAvailability.data, pageAvailability.isLoaded]);

  const value = useMemo<ContextType>(
    () => ({
      setCarouselVehicles,
      setRecentVehicles,
      setSuggestedVehicles,
      triggerVehicleEvent,
      vehicles: new Array<MegaEventVehicleWithBelongsTo>().concat(
        carouselVehicles,
        recentVehicles,
        suggestedVehicles
      )
    }),
    [
      carouselVehicles,
      recentVehicles,
      setCarouselVehicles,
      setRecentVehicles,
      setSuggestedVehicles,
      suggestedVehicles,
      triggerVehicleEvent
    ]
  );

  return (
    <MegaEventContext.Provider value={value}>
      {children}
    </MegaEventContext.Provider>
  );
}

export default MegaEventContext;
